import apiClient, { unwrap } from './client';
import { cache, CACHE_TAGS, getModuleTtl } from '../utils/cache';

const bustAnimals = () => cache.invalidateByTag(CACHE_TAGS.DAIRY_ANIMALS);

// ── Sheds ───────────────────────────────────────────────────────────────────

export const dairyShedApi = {
  getAll: async () => {
    const res = await apiClient.get('/dairy/sheds');
    return unwrap(res) ?? [];
  },

  getById: async (id) => {
    const res = await apiClient.get(`/dairy/sheds/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const res = await apiClient.post('/dairy/sheds', data);
    bustAnimals(); // animal cards show the shed name
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/dairy/sheds/${id}`, data);
    bustAnimals(); // animal cards show the shed name
    return unwrap(res);
  },

  delete: async (id) => {
    await apiClient.delete(`/dairy/sheds/${id}`);
    bustAnimals();
  },
};

// ── Animals ─────────────────────────────────────────────────────────────────

export const dairyAnimalApi = {
  // Eight pages load the whole herd (breeding enrichment included). Cache it briefly; every
  // animal/shed/breeding/sale mutation below busts it, and the cache is cleared on login/logout.
  getAll: async () => {
    const key = 'dairy-animals-all';
    const cached = cache.get(key);
    if (cached) return [...cached];
    const res = await apiClient.get('/dairy/animals');
    const data = unwrap(res) ?? [];
    cache.set(key, data, { ttl: getModuleTtl(CACHE_TAGS.DAIRY_ANIMALS), tags: [CACHE_TAGS.DAIRY_ANIMALS] });
    return [...data];
  },

  getAllPaged: async (page = 0, size = 20) => {
    const res = await apiClient.get('/dairy/animals/paged', { params: { page, size, sort: 'name,asc' } });
    const data = unwrap(res);
    return data?.content ?? data ?? [];
  },

  getByShed: async (shedId) => {
    const res = await apiClient.get(`/dairy/animals/by-shed/${shedId}`);
    return unwrap(res) ?? [];
  },

  getById: async (id) => {
    const res = await apiClient.get(`/dairy/animals/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const res = await apiClient.post('/dairy/animals', data);
    bustAnimals();
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/dairy/animals/${id}`, data);
    bustAnimals();
    return unwrap(res);
  },

  delete: async (id) => {
    await apiClient.delete(`/dairy/animals/${id}`);
    bustAnimals();
  },

  getOffspring: async (id) => {
    const res = await apiClient.get(`/dairy/animals/${id}/offspring`);
    return unwrap(res) ?? [];
  },

  getLineage: async (id) => {
    const res = await apiClient.get(`/dairy/animals/${id}/lineage`);
    return unwrap(res);
  },
};

// ── Milk Records ─────────────────────────────────────────────────────────────

export const milkRecordApi = {
  getAllPaged: async (page = 0, size = 30) => {
    const res = await apiClient.get('/dairy/milk-records', { params: { page, size, sort: 'recordDate,desc' } });
    const data = unwrap(res);
    return data?.content ?? data ?? [];
  },

  getByAnimal: async (animalId) => {
    const res = await apiClient.get(`/dairy/milk-records/by-animal/${animalId}`);
    return unwrap(res) ?? [];
  },

  // Inclusive date range (max 62 days) — used by the milking-round sheet so it never
  // depends on how many records the paged list happens to have loaded.
  getRange: async (from, to) => {
    const res = await apiClient.get('/dairy/milk-records/range', { params: { from, to } });
    return unwrap(res) ?? [];
  },

  // One milking round: { recordDate, session, entries: [{ animalId, quantityLiters }] }.
  // Atomic, and re-saving updates existing rows instead of duplicating them.
  saveBulk: async (payload) => {
    const res = await apiClient.post('/dairy/milk-records/bulk', payload);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK); // posts production to the stock ledger
    return unwrap(res);
  },

  getById: async (id) => {
    const res = await apiClient.get(`/dairy/milk-records/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const res = await apiClient.post('/dairy/milk-records', data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/dairy/milk-records/${id}`, data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  delete: async (id) => {
    await apiClient.delete(`/dairy/milk-records/${id}`);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
  },
};

// ── Milk Stock (production/sale/self-use/wastage ledger) ──────────────────────

export const milkStockApi = {
  /**
   * Day-by-day reconciliation (most recent first) + insights. Rolling window via `days` (max 90),
   * or an explicit `from`/`to` (YYYY-MM-DD, max 90 days — e.g. last month). With `days`, days[0] is today.
   */
  getReconciliation: async ({ days, from, to } = {}) => {
    // Dashboard, Sales, Sell-Milk modal and the Milk page all read this. Cache per window; every
    // milk record / sale / purchase / payment / stock mutation invalidates MILK_STOCK (2 min TTL otherwise).
    const key = `milk-stock-reconciliation:${days ?? ''}:${from ?? ''}:${to ?? ''}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const res = await apiClient.get('/dairy/milk-stock/reconciliation', { params: { days, from, to } });
    const data = unwrap(res);
    cache.set(key, data, { ttl: getModuleTtl(CACHE_TAGS.MILK_STOCK), tags: [CACHE_TAGS.MILK_STOCK] });
    return data;
  },

  getMovements: async ({ animalType, type, source, from, to, page = 0, size = 30 } = {}) => {
    const res = await apiClient.get('/dairy/milk-stock/movements', {
      params: { animalType, type, source, from, to, page, size, sort: 'movementDate,desc' },
    });
    const data = unwrap(res);
    return data?.content ?? data ?? [];
  },

  /** type: 'SELF_USE' | 'WASTED' */
  addMovement: async (data) => {
    const res = await apiClient.post('/dairy/milk-stock/movements', data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  /** Editing/deleting is only allowed for manually recorded (SELF_USE/WASTED) entries. */
  updateMovement: async (id, data) => {
    const res = await apiClient.put(`/dairy/milk-stock/movements/${id}`, data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  deleteMovement: async (id) => {
    await apiClient.delete(`/dairy/milk-stock/movements/${id}`);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
  },

  /** Cumulative resale/trading stock balance per animal type — a real running balance, unlike getReconciliation's same-day-only view. */
  getTradeBalance: async ({ since } = {}) => {
    const key = `milk-stock-trade-balance:${since ?? ''}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const res = await apiClient.get('/dairy/milk-stock/trade-balance', { params: { since } });
    const data = unwrap(res);
    cache.set(key, data, { ttl: getModuleTtl(CACHE_TAGS.MILK_STOCK), tags: [CACHE_TAGS.MILK_STOCK] });
    return data;
  },
};

// ── Milk Trading: Sellers/Customers ───────────────────────────────────────────

export const tradePartyApi = {
  getAll: async () => {
    const res = await apiClient.get('/dairy/trade-parties');
    return unwrap(res) ?? [];
  },

  getById: async (id) => {
    const res = await apiClient.get(`/dairy/trade-parties/${id}`);
    return unwrap(res);
  },

  getStatement: async (id) => {
    const res = await apiClient.get(`/dairy/trade-parties/${id}/statement`);
    return unwrap(res);
  },

  create: async (data) => {
    const res = await apiClient.post('/dairy/trade-parties', data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/dairy/trade-parties/${id}`, data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  delete: async (id) => {
    await apiClient.delete(`/dairy/trade-parties/${id}`);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
  },
};

// ── Milk Trading: Purchases (buying from a seller for resale) ─────────────

export const milkPurchaseApi = {
  getAllPaged: async ({ partyId, from, to, page = 0, size = 30 } = {}) => {
    const res = await apiClient.get('/dairy/milk-purchases', {
      params: { partyId, from, to, page, size, sort: 'purchaseDate,desc' },
    });
    const data = unwrap(res);
    return data?.content ?? data ?? [];
  },

  getById: async (id) => {
    const res = await apiClient.get(`/dairy/milk-purchases/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const res = await apiClient.post('/dairy/milk-purchases', data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/dairy/milk-purchases/${id}`, data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },

  delete: async (id) => {
    await apiClient.delete(`/dairy/milk-purchases/${id}`);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
  },
};

// ── Milk Trading: Payments (either direction, or a standalone advance) ──────

export const tradePaymentApi = {
  getByParty: async (partyId) => {
    const res = await apiClient.get('/dairy/trade-payments', { params: { partyId } });
    return unwrap(res) ?? [];
  },

  getByPurchase: async (purchaseId) => {
    const res = await apiClient.get('/dairy/trade-payments', { params: { purchaseId } });
    return unwrap(res) ?? [];
  },

  /** { partyId, purchaseId?, saleId?, direction, paymentDate, amount, paymentMethod, notes? } */
  record: async (data) => {
    const res = await apiClient.post('/dairy/trade-payments', data);
    cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);
    return unwrap(res);
  },
};

// ── Breeding Records ─────────────────────────────────────────────────────────

export const breedingRecordApi = {
  getByAnimal: async (animalId) => {
    const res = await apiClient.get(`/dairy/breeding-records/by-animal/${animalId}`);
    return unwrap(res) ?? [];
  },

  getUpcomingCalvings: async (days = 30) => {
    const res = await apiClient.get('/dairy/breeding-records/upcoming-calvings', { params: { days } });
    return unwrap(res) ?? [];
  },

  getById: async (id) => {
    const res = await apiClient.get(`/dairy/breeding-records/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const res = await apiClient.post('/dairy/breeding-records', data);
    bustAnimals();
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/dairy/breeding-records/${id}`, data);
    bustAnimals();
    return unwrap(res);
  },

  delete: async (id) => {
    await apiClient.delete(`/dairy/breeding-records/${id}`);
    bustAnimals();
  },
};

// ── Health Records (shared livestock) ───────────────────────────────────────

export const healthRecordApi = {
  getAll: async (farmModule) => {
    const res = await apiClient.get('/livestock/health-records', { params: { farmModule } });
    return unwrap(res) ?? [];
  },

  getByAnimal: async (farmModule, animalId) => {
    const res = await apiClient.get(`/livestock/health-records/by-animal/${animalId}`, { params: { farmModule } });
    return unwrap(res) ?? [];
  },

  getUpcomingDue: async (farmModule, days = 30) => {
    const res = await apiClient.get('/livestock/health-records/upcoming-due', { params: { farmModule, days } });
    return unwrap(res) ?? [];
  },

  getById: async (id) => {
    const res = await apiClient.get(`/livestock/health-records/${id}`);
    return unwrap(res);
  },

  create: async (data) => {
    const res = await apiClient.post('/livestock/health-records', data);
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/livestock/health-records/${id}`, data);
    return unwrap(res);
  },

  delete: async (id) => {
    await apiClient.delete(`/livestock/health-records/${id}`);
  },
};

// ── Farm Modules ─────────────────────────────────────────────────────────────

export const farmModuleApi = {
  listModules: async () => {
    const res = await apiClient.get('/farm-modules');
    return unwrap(res) ?? [];
  },

  activateFree: async (moduleType) => {
    const res = await apiClient.post(`/farm-modules/${moduleType}/activate`);
    return unwrap(res);
  },

  /**
   * Set or clear renewal exclusion for a module.
   * When exclude=true, the module will not renew at the next billing cycle
   * but stays active until its current subscription_ends_at.
   */
  setExcludeFromRenewal: async (moduleType, exclude) => {
    const res = await apiClient.patch(`/farm-modules/${moduleType}/renewal-exclusion`, { exclude });
    return unwrap(res);
  },

  // ── Module billing ──────────────────────────────────────────────────────────

  /** Get country-localised pricing tiers for a module (e.g. 'DAIRY'). */
  getModulePricing: async (moduleType) => {
    const res = await apiClient.get(`/farm-modules/${moduleType}/pricing`);
    return unwrap(res) ?? [];
  },

  /**
   * Create a Razorpay order to upgrade a module plan.
   * @param {string} moduleType  e.g. 'DAIRY'
   * @param {{ planCode: string, durationMonths: number, promoCode?: string }} payload
   * @returns {Promise<{ orderId, razorpayOrderId, amount, currency, key, totalAmount, isProrated, ... }>}
   */
  createModuleOrder: async (moduleType, payload) => {
    const res = await apiClient.post(`/farm-modules/${moduleType}/orders`, payload);
    return unwrap(res);
  },

  /** Server-side promo pricing for a module purchase (no order is created). */
  previewModulePromo: async (moduleType, payload) => {
    const res = await apiClient.post(`/farm-modules/${moduleType}/orders/promo-preview`, payload);
    return unwrap(res);
  },

  /**
   * Verify payment and activate the module plan.
   * @param {string} moduleType
   * @param {{ razorpayOrderId, razorpayPaymentId, razorpaySignature }} payload
   */
  verifyModulePayment: async (moduleType, payload) => {
    const res = await apiClient.post(`/farm-modules/${moduleType}/orders/verify`, payload);
    return unwrap(res);
  },

  /** List module subscription order history. */
  getModuleOrders: async (moduleType) => {
    const res = await apiClient.get(`/farm-modules/${moduleType}/orders`);
    return unwrap(res) ?? [];
  },
};

// ── Milk deliveries: workers log them (no prices), owner/admin approves into a sale ──

// Every change moves reserved litres and, on approval, creates a sale — keep the stock views fresh.
const bustDeliveries = () => cache.invalidateByTag(CACHE_TAGS.MILK_STOCK);

export const milkDispatchApi = {
  /** Workers get their own deliveries only. `status` is an array of statuses; omit for all. */
  list: async ({ status, from, to, size = 100 } = {}) => {
    const res = await apiClient.get('/dairy/milk-dispatches', {
      params: { status: status?.length ? status.join(',') : undefined, from, to, size },
    });
    const data = unwrap(res);
    return data?.content ?? data ?? [];
  },

  /** { outForDelivery, awaitingApproval, pending: [{ date, animalType, liters }] } — litres/counts only, any role. */
  getSummary: async ({ from, to } = {}) => {
    const key = `milk-dispatch-summary:${from ?? ''}:${to ?? ''}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const res = await apiClient.get('/dairy/milk-dispatches/summary', { params: { from, to } });
    const data = unwrap(res);
    cache.set(key, data, { ttl: getModuleTtl(CACHE_TAGS.MILK_STOCK), tags: [CACHE_TAGS.MILK_STOCK] });
    return data;
  },

  /** Active customers as { id, name } — no balances or prices, so workers may call it. */
  getCustomers: async () => {
    const res = await apiClient.get('/dairy/milk-dispatches/customers');
    return unwrap(res) ?? [];
  },

  create: async (data) => {
    const res = await apiClient.post('/dairy/milk-dispatches', data);
    bustDeliveries();
    return unwrap(res);
  },

  update: async (id, data) => {
    const res = await apiClient.put(`/dairy/milk-dispatches/${id}`, data);
    bustDeliveries();
    return unwrap(res);
  },

  markDelivered: async (id, deliveredQuantity) => {
    const res = await apiClient.post(`/dairy/milk-dispatches/${id}/deliver`, deliveredQuantity != null ? { deliveredQuantity } : {});
    bustDeliveries();
    return unwrap(res);
  },

  /** The customer refused milk. Omit returnedQuantity for all of it. It becomes available stock again. */
  returnMilk: async (id, { returnedQuantity, reason, notes }) => {
    const res = await apiClient.post(`/dairy/milk-dispatches/${id}/return`, { returnedQuantity, reason, notes });
    bustDeliveries();
    return unwrap(res);
  },

  cancel: async (id) => {
    await apiClient.delete(`/dairy/milk-dispatches/${id}`);
    bustDeliveries();
  },

  /** Owner/admin. Empty body = agreed price, delivery date, on credit for a linked customer. */
  approve: async (id, data = {}) => {
    const res = await apiClient.post(`/dairy/milk-dispatches/${id}/approve`, data);
    bustDeliveries();
    return unwrap(res);
  },

  /** Owner/admin. Returns { approved, skipped: [{ id, reason }] }. */
  approveAll: async (ids) => {
    const res = await apiClient.post('/dairy/milk-dispatches/approve-all', { ids });
    bustDeliveries();
    return unwrap(res);
  },

  reject: async (id, reason) => {
    const res = await apiClient.post(`/dairy/milk-dispatches/${id}/reject`, { reason });
    bustDeliveries();
    return unwrap(res);
  },
};
